import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { autoSelectFarm, formatNumber, useGetFarms, useGetFieldsByFarmId, useTranslation, useUserContext } from "utils";

import { SOIL_SIGHT_DRAWER_WIDTH } from "consts";

import { FarmResponse, FieldListField } from "models";

import { Drawer, FieldSelector, Icon, InputSearch, Typography } from "components";

import { FarmDetails } from "pages/app/farm/components/FarmDetails";

const farmTotalHa = (fieldList: FieldListField[]) => fieldList.reduce((sum, f) => sum + (typeof f.size === "number" ? f.size : 0), 0);

export type FarmFieldSelectorProps = {
  selectedFarmId: string | null;
  selectedFieldId: string | null;
  setSelectedFarmId: (id: string | null) => void;
  setSelectedFieldId: (id: string | null) => void;
  onFieldAdded?: (fieldId: string) => void;
};

/**
 * The farm + field dropdown cards of the SoilSight onboarding (search, add-farm and
 * add-field drawers, auto-selection of the active farm and largest field). Extracted
 * so the get-started flow and any future caller share one implementation.
 */
export const FarmFieldSelector = ({ selectedFarmId, selectedFieldId, setSelectedFarmId, setSelectedFieldId, onFieldAdded }: FarmFieldSelectorProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { user } = useUserContext();

  const [farmOpen, setFarmOpen] = useState(false);
  const [farmSearch, setFarmSearch] = useState("");
  const [showAddFarmDrawer, setShowAddFarmDrawer] = useState(false);

  const farmRef = useRef<HTMLDivElement>(null);

  const { data: farms = [], isLoading: farmsLoading, refetch: refetchFarms } = useGetFarms();
  // Fields for the selected farm are also fetched inside FieldSelector; React Query dedupes the shared
  // key, so this second call is only used here for the farm's total-hectares sub-line.
  const { data: fields = [] } = useGetFieldsByFarmId({
    farmId: selectedFarmId ?? "",
    enabled: !!selectedFarmId,
  });

  useEffect(() => {
    if (farmsLoading || !selectedFarmId) return;
    if (!farms.some((f) => f.id === selectedFarmId)) setSelectedFarmId(null);
  }, [farms, farmsLoading, selectedFarmId, setSelectedFarmId]);

  // Preselect the farm currently active on the platform when arriving with nothing selected.
  useEffect(() => {
    if (farmsLoading || farms.length === 0 || selectedFarmId || !user?.farm?.id) return;
    const match = autoSelectFarm(farms, user.farm.id);
    if (match) setSelectedFarmId(match.id);
  }, [farmsLoading, farms, selectedFarmId, user?.farm?.id, setSelectedFarmId]);

  // Click-outside farm dropdown
  useEffect(() => {
    if (!farmOpen) return undefined;
    const handle = (e: MouseEvent) => {
      if (farmRef.current && !farmRef.current.contains(e.target as Node)) {
        setFarmOpen(false);
        setFarmSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [farmOpen]);

  const handleFarmSelect = useCallback(
    (farmId: string) => {
      setSelectedFarmId(farmId);
      setFarmOpen(false);
      setFarmSearch("");
    },
    [setSelectedFarmId]
  );

  const handleFarmAdded = useCallback(async () => {
    setShowAddFarmDrawer(false);
    const { data: updated } = await refetchFarms();
    if (!updated || updated.length === 0) return;
    const newest = [...updated].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    setSelectedFarmId(newest.id);
  }, [refetchFarms, setSelectedFarmId]);

  const filteredFarms = useMemo(() => farms.filter((f: FarmResponse) => f.name.toLowerCase().includes(farmSearch.toLowerCase())), [farms, farmSearch]);

  const selectedFarm = useMemo(() => farms.find((f: FarmResponse) => f.id === selectedFarmId) ?? null, [farms, selectedFarmId]);
  const totalFarmHa = useMemo(() => farmTotalHa(fields), [fields]);

  const farmSubLine = selectedFarm
    ? [selectedFarm.region || null, totalFarmHa > 0 ? `${formatNumber({ number: totalFarmHa, decimalPlaces: 2 })} ha` : null].filter(Boolean).join(" · ")
    : "";

  return (
    <>
      {/* Farm selector */}
      <div ref={farmRef} className="farm-field-step__card-wrapper">
        <div
          className="farm-field-step__card-trigger"
          data-testid="scenario-onboarding-farm-select"
          role="button"
          tabIndex={0}
          onClick={() => setFarmOpen((prev) => !prev)}
          onKeyDown={(e) => e.key === "Enter" && setFarmOpen((prev) => !prev)}
        >
          <div className={`farm-field-step__card-icon${selectedFarm ? " farm-field-step__card-icon--farm" : ""}`}>
            <Icon iconName="klim/farm" size="m" />
          </div>
          <div className="farm-field-step__card-content">
            {selectedFarm ? (
              <>
                <span className="farm-field-step__card-name">{selectedFarm.name}</span>
                {farmSubLine && <span className="farm-field-step__card-sub">{farmSubLine}</span>}
              </>
            ) : (
              <span className="farm-field-step__card-placeholder">{t("onboarding.step1.farmPlaceholder")}</span>
            )}
          </div>
          <Icon iconName={farmOpen ? "tabler/chevron-up" : "tabler/chevron-down"} size="s" className="farm-field-step__card-chevron" />
        </div>

        {farmOpen && (
          <div className="farm-field-step__dropdown-panel">
            <div className="farm-field-step__dropdown-search-row">
              <InputSearch
                autoFocus
                className="farm-field-step__dropdown-search-input"
                placeholder={t("onboarding.step1.searchFarmPlaceholder")}
                value={farmSearch}
                onChange={(e) => setFarmSearch(e.target.value)}
                allowClear={false}
                variant="borderless"
                dataTestId="scenario-onboarding-farm-search"
              />
            </div>

            {filteredFarms.length > 0 ? (
              <ul className="farm-field-step__dropdown-list" role="listbox">
                {filteredFarms.map((farm: FarmResponse) => {
                  const sub = [
                    farm.region || null,
                    farm.id === selectedFarmId && totalFarmHa > 0 ? `${formatNumber({ number: totalFarmHa, decimalPlaces: 2 })} ha` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");

                  return (
                    <li
                      key={farm.id}
                      role="option"
                      tabIndex={0}
                      aria-selected={farm.id === selectedFarmId}
                      className={`farm-field-step__dropdown-row${farm.id === selectedFarmId ? " farm-field-step__dropdown-row--selected" : ""}`}
                      onClick={() => handleFarmSelect(farm.id)}
                      onKeyDown={(e) => e.key === "Enter" && handleFarmSelect(farm.id)}
                    >
                      <div className={`farm-field-step__option-icon${farm.id === selectedFarmId ? " farm-field-step__option-icon--farm" : ""}`}>
                        <Icon iconName="klim/farm" size="m" />
                      </div>
                      <div className="farm-field-step__option-body">
                        <span className="farm-field-step__option-name">{farm.name}</span>
                        {sub && <span className="farm-field-step__option-sub">{sub}</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="farm-field-step__dropdown-empty">
                <Icon iconName="klim/farm" size="xl" className="farm-field-step__dropdown-empty-icon" />
                <div className="farm-field-step__dropdown-empty-text">
                  <Typography variant="paragraph-m" className="farm-field-step__dropdown-empty-title">
                    {t("onboarding.step1.noFarmsTitle")}
                  </Typography>
                  <Typography variant="paragraph-s" color="grey-600">
                    {t("onboarding.step1.noFarmsSubtitle")}
                  </Typography>
                </div>
              </div>
            )}

            <div
              className="farm-field-step__dropdown-add-row"
              role="button"
              tabIndex={0}
              onClick={() => {
                setFarmOpen(false);
                setShowAddFarmDrawer(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setFarmOpen(false);
                  setShowAddFarmDrawer(true);
                }
              }}
            >
              <div className="farm-field-step__dropdown-add-icon">
                <Icon iconName="klim/plus" size="s" />
              </div>
              <span className="farm-field-step__dropdown-add-label">{t("onboarding.step1.addFarmCta")}</span>
            </div>
          </div>
        )}
      </div>

      {/* Field selector (shared with the scenario field-reselection modal) */}
      <FieldSelector farmId={selectedFarmId} selectedFieldId={selectedFieldId} setSelectedFieldId={setSelectedFieldId} onFieldAdded={onFieldAdded} />

      <Drawer
        title={t("onboarding.step1.addFarmDrawerTitle")}
        onClose={() => setShowAddFarmDrawer(false)}
        width={SOIL_SIGHT_DRAWER_WIDTH}
        visible={showAddFarmDrawer}
      >
        <FarmDetails selectedFarmId="" isOnboarding={false} handleBack={() => setShowAddFarmDrawer(false)} onFinish={handleFarmAdded} />
      </Drawer>
    </>
  );
};
